<template>
  <Card headless>
    <div class="section-spacing-15">
      <div class="flex flex-col xl:flex-row xl:items-center gap-3">
        <div>
          <div class="color-pill" :style="pillStyle">Example Text</div>
        </div>
        <label :for="`colorPicker-${customization.id}`">
          <div
            v-if="editing"
            class="link"
            :data-cy="`${customization.key}CancelLink`"
            @click="toggleEditing"
          >
            <i class="far fa-times mr-1"></i>
            Cancel
          </div>
          <div
            v-else
            class="link"
            :data-cy="`${customization.key}EditLink`"
            @click="toggleEditing"
          >
            <i class="far fa-pen-to-square mr-1"></i>
            Edit Color
          </div>
        </label>
      </div>
      <div>
        <div class="text-title mb-2">{{ label }}</div>
        <div class="text-body">{{ customization.description }}</div>
      </div>
      <div :class="{ hidden: !editing }">
        <div class="color-input-cont mr-3">
          <input
            v-if="editing"
            :id="`colorPicker-${customization.id}`"
            ref="colorInput"
            v-model="color"
            name="color"
            type="color"
            class="color-input-text"
            :data-cy="`${customization.key}ColorPicker`"
          />
        </div>
        <TextField
          class="mt-3"
          v-model="color"
          label="Color"
          name="color"
          type="text"
          :placeholder="customization.default"
          no-label
          :data-cy="`${customization.key}ColorInput`"
        />
      </div>
      <div
        v-if="editing"
        class="d-flex justify-content-between align-items-center actions-cont"
      >
        <div
          class="link"
          :data-cy="`${customization.key}ResetLink`"
          @click="resetColor"
        >
          <i class="far fa-arrow-rotate-left mr-1"></i>
          Reset to Default
        </div>
        <Button
          outline
          icon="far fa-pen-to-square"
          text="Save"
          :data-cy="`${customization.key}Save`"
          @click="handleSave"
        />
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import Button from "@/components/Button.vue";
import Card from "@/components/Card.vue";
import TextField from "@/components/TextField.vue";
import { CustomizationRecord } from "./customizations";

const labelMap = {
  pending_unassigned: "Pending (Unassigned)",
  pending_assigned: "Pending (Assigned)",
  accepted_pre_work: "Accepted (Pre-Work)",
  accepted: "Accepted (Start Trip)",
  running: "Running",
  compeleted_lost_opportunity: "Completed (Lost Opportunity)",
  compeleted_won_opportunity: "Completed (Won Opportunity)"
};

const props = defineProps<{
  customization: CustomizationRecord;
}>();
const emit = defineEmits(["reset-default", "save"]);

const editing = ref(false);
const color = ref(props.customization.value || props.customization.default);
const colorInput = ref(null);

const pillStyle = computed(() => ({
  background: color.value
}));

const label = computed(() => labelMap[props.customization.key]);

function toggleEditing() {
  editing.value = !editing.value;
}

function resetColor() {
  color.value = props.customization.default;
  emit("reset-default", props.customization);
  editing.value = false;
}

function handleSave() {
  emit("save", { ...props.customization, value: color.value });
  editing.value = false;
}

watch(editing, (newValue) => {
  if (!newValue) {
    setTimeout(() => {
      const currentColor =
        props.customization.value || props.customization.default;
      if (color.value !== currentColor) {
        color.value = currentColor;
      }
    }, 500);
  }
});

watch(
  () => props.customization,
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue) {
      color.value = newValue.value || newValue.default;
    }
  }
);
</script>

<style scoped>
.color-pill {
  border-radius: 4px;
  color: #fff;
  width: 140px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.45);
}

.v1 .color-pill {
  border-radius: 25px;
  width: 160px;
  height: 36px;
}
.color-input-text {
  appearance: none;
  border: none;
  padding: 0;
  margin: 0;
  width: 50px;
  height: 50px;
  cursor: pointer;
  position: absolute;
  top: -8px;
  left: -8px;
  margin-bottom: -13px;
}
.color-input-cont {
  position: relative;
  border: 2px solid var(--neutral-gray);
  border-radius: 50%;
  overflow: hidden;
  height: 35px;
  width: 35px;
}
.text-title {
  font-size: 16px;
}
.actions-cont {
  padding: 10px;
  background: var(--neutral-gray);
}
.example-text {
  font-size: 1.1em;
}
</style>
